"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { Check, Send } from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { type ContactFormData, contactSchema, SUBJECT_OPTIONS } from "@/lib/contact"

export function ContactForm() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { toast } = useToast()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      subject: undefined, // Will trigger validation error if not selected
      message: "",
      website: "", // Honeypot field
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })

      const result = (await response.json()) as { success?: boolean; error?: string; message?: string }

      if (!response.ok) {
        throw new Error(result.error || "Failed to send message")
      }

      setIsSubmitted(true)
      reset()
      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
      })
    } catch (error) {
      console.error("Contact form error:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message. Please try again.",
      })
    }
  }

  if (isSubmitted) {
    return (
      <Card className="w-full max-w-lg border-white/10 bg-neutral-900/80 backdrop-blur-md">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
            <Check className="h-8 w-8 text-green-500" />
          </div>
          <h3 className="mb-2 text-xl font-semibold text-white">Message Sent!</h3>
          <p className="mb-6 text-center text-white/70">
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
          <Button
            onClick={() => setIsSubmitted(false)}
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10"
          >
            Send Another Message
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-lg border-white/10 bg-neutral-900/80 text-left backdrop-blur-md">
      <CardHeader>
        <CardTitle className="text-xl text-white">Get in Touch</CardTitle>
        <CardDescription className="text-white/70">
          Have a question or want to work with us? Fill out the form below.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Honeypot field - hidden from real users, bots will fill it */}
          <input
            type="text"
            {...register("website")}
            className="absolute -left-[9999px] opacity-0"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm text-white">
              Name <span className="text-red-400">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              {...register("name")}
              className="h-11 border-neutral-700 bg-neutral-800 text-white"
              placeholder="Your full name"
            />
            {errors.name && <p className="text-sm text-red-400">{errors.name.message}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-white">
              Email <span className="text-red-400">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              className="h-11 border-neutral-700 bg-neutral-800 text-white"
              placeholder="your@email.com"
            />
            {errors.email && <p className="text-sm text-red-400">{errors.email.message}</p>}
          </div>

          {/* Phone (optional) */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm text-white">
              Phone <span className="text-white/50">(optional)</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              {...register("phone")}
              className="h-11 border-neutral-700 bg-neutral-800 text-white"
              placeholder="Your phone number"
            />
          </div>

          {/* Company (optional) */}
          <div className="space-y-2">
            <Label htmlFor="company" className="text-sm text-white">
              Company <span className="text-white/50">(optional)</span>
            </Label>
            <Input
              id="company"
              type="text"
              {...register("company")}
              className="h-11 border-neutral-700 bg-neutral-800 text-white"
              placeholder="Your company name"
            />
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-sm text-white">
              Subject <span className="text-red-400">*</span>
            </Label>
            <select
              id="subject"
              {...register("subject")}
              className="h-11 w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 text-white focus:border-neutral-500 focus:ring-1 focus:ring-neutral-500 focus:outline-none"
            >
              <option value="">Select a subject...</option>
              {SUBJECT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.subject && <p className="text-sm text-red-400">{errors.subject.message}</p>}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm text-white">
              Message <span className="text-red-400">*</span>
            </Label>
            <textarea
              id="message"
              {...register("message")}
              rows={5}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-white placeholder:text-neutral-500 focus:border-neutral-500 focus:ring-1 focus:ring-neutral-500 focus:outline-none"
              placeholder="How can we help you?"
            />
            {errors.message && <p className="text-sm text-red-400">{errors.message.message}</p>}
          </div>

          {/* Submit */}
          <Button
            type="submit"
            loading={isSubmitting}
            className="h-11 w-full bg-gradient-to-r from-indigo-600 to-rose-500 hover:from-indigo-700 hover:to-rose-600"
          >
            {!isSubmitting && <Send className="mr-2 h-4 w-4" />}
            {isSubmitting ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
